import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { getEnv } from '../config';

const API_URL = getEnv('REACT_APP_API_URL') || '';

// ── Constants ────────────────────────────────────────────────────────────────

const DAYS = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' },
];

const ALL_DAY_VALUES = DAYS.map(d => d.value);

const TIMES = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'evening', label: 'Evening' },
];

const ALL_TIME_VALUES = TIMES.map(t => t.value);

const FORMATS = [
    { value: 'in_person', label: 'In Person' },
    { value: 'zoom', label: 'Zoom' },
];

const EXPERIENCE = [
    { value: 'beginner', label: 'New to the Bible' },
    { value: 'experienced', label: 'Some Experience' },
];

// ── Reusable UI Components ───────────────────────────────────────────────────

function Chip({ selected, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                selected
                    ? 'bg-accent-500 border-accent-500 text-white shadow-lg shadow-accent-500/25'
                    : 'bg-slate-700/50 border-slate-600/50 text-slate-300 hover:bg-slate-600/50 hover:border-slate-500/50'
            }`}
        >
            {children}
        </button>
    );
}

function FlexibleChip({ selected, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                selected
                    ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-slate-700/50 border-slate-600/50 text-slate-300 hover:bg-slate-600/50 hover:border-slate-500/50'
            }`}
        >
            Flexible
        </button>
    );
}

function RadioChip({ selected, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex-1 min-w-[120px] px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-200 ${
                selected
                    ? 'bg-accent-500 border-accent-500 text-white shadow-lg shadow-accent-500/25'
                    : 'bg-slate-700/50 border-slate-600/50 text-slate-300 hover:bg-slate-600/50 hover:border-slate-500/50'
            }`}
        >
            {children}
        </button>
    );
}

function FieldError({ error }) {
    if (!error) return null;
    return <p className="mt-1.5 text-red-400 text-sm">{error}</p>;
}

function InputField({ label, required, error, ...props }) {
    return (
        <div>
            <label className="block text-sm font-medium text-slate-400 mb-1.5">
                {label}{required && <span className="text-accent-400 ml-0.5">*</span>}
            </label>
            <input
                {...props}
                className={`w-full px-4 py-3 rounded-xl bg-slate-700/50 border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 transition-all duration-200 ${
                    error
                        ? 'border-red-400/50 focus:ring-red-400/30 focus:border-red-400/50'
                        : 'border-slate-600/50 focus:ring-accent-500/30 focus:border-accent-500/50'
                }`}
            />
            <FieldError error={error} />
        </div>
    );
}

// ── Progress Bar ─────────────────────────────────────────────────────────────

function ProgressBar({ step, total }) {
    return (
        <div className="flex gap-2 mb-8">
            {Array.from({ length: total }, (_, i) => (
                <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-700/50">
                    <div
                        className={`h-full rounded-full transition-all duration-500 ease-out ${
                            i < step ? 'bg-accent-400' : ''
                        }`}
                        style={{ width: i < step ? '100%' : '0%' }}
                    />
                </div>
            ))}
        </div>
    );
}

// ── Form Steps ───────────────────────────────────────────────────────────────

function StepOne({ formData, setFormData, errors }) {
    const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-xl font-bold font-serif text-white mb-1">Tell us about yourself</h3>
                <p className="text-slate-400 text-sm">We'll use this to connect you with a study group.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                    label="First Name"
                    required
                    type="text"
                    value={formData.first_name}
                    onChange={e => update('first_name', e.target.value)}
                    placeholder="John"
                    error={errors.first_name}
                    autoFocus
                />
                <InputField
                    label="Last Name"
                    required
                    type="text"
                    value={formData.last_name}
                    onChange={e => update('last_name', e.target.value)}
                    placeholder="Doe"
                    error={errors.last_name}
                />
            </div>
            <InputField
                label="Phone"
                required
                type="tel"
                value={formData.phone}
                onChange={e => update('phone', e.target.value)}
                placeholder="(555) 555-1234"
                error={errors.phone}
            />
            <InputField
                label="Email"
                type="email"
                value={formData.email}
                onChange={e => update('email', e.target.value)}
                placeholder="john@example.com"
                error={errors.email}
            />
        </div>
    );
}

function StepTwo({ formData, setFormData, errors }) {
    const daysFlexible = ALL_DAY_VALUES.every(d => formData.preferred_days.includes(d));
    const timesFlexible = ALL_TIME_VALUES.every(t => formData.preferred_times.includes(t));

    const toggleDay = (day) => {
        setFormData(prev => ({
            ...prev,
            preferred_days: prev.preferred_days.includes(day)
                ? prev.preferred_days.filter(d => d !== day)
                : [...prev.preferred_days, day],
        }));
    };

    const toggleDaysFlexible = () => {
        setFormData(prev => ({
            ...prev,
            preferred_days: daysFlexible ? [] : [...ALL_DAY_VALUES],
        }));
    };

    const toggleTime = (time) => {
        setFormData(prev => ({
            ...prev,
            preferred_times: prev.preferred_times.includes(time)
                ? prev.preferred_times.filter(t => t !== time)
                : [...prev.preferred_times, time],
        }));
    };

    const toggleTimesFlexible = () => {
        setFormData(prev => ({
            ...prev,
            preferred_times: timesFlexible ? [] : [...ALL_TIME_VALUES],
        }));
    };

    const toggleFormat = (fmt) => {
        setFormData(prev => ({
            ...prev,
            preferred_formats: prev.preferred_formats.includes(fmt)
                ? prev.preferred_formats.filter(f => f !== fmt)
                : [...prev.preferred_formats, fmt],
        }));
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-xl font-bold font-serif text-white mb-1">Your preferences</h3>
                <p className="text-slate-400 text-sm">Help us find the best fit for you.</p>
            </div>

            {/* Days */}
            <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                    Preferred Days
                </label>
                <div className="flex flex-wrap gap-2">
                    {DAYS.map(d => (
                        <Chip
                            key={d.value}
                            selected={formData.preferred_days.includes(d.value)}
                            onClick={() => toggleDay(d.value)}
                        >
                            {d.label}
                        </Chip>
                    ))}
                    <FlexibleChip selected={daysFlexible} onClick={toggleDaysFlexible} />
                </div>
            </div>

            {/* Times */}
            <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                    Preferred Times
                </label>
                <div className="flex flex-wrap gap-2">
                    {TIMES.map(t => (
                        <Chip
                            key={t.value}
                            selected={formData.preferred_times.includes(t.value)}
                            onClick={() => toggleTime(t.value)}
                        >
                            {t.label}
                        </Chip>
                    ))}
                    <FlexibleChip selected={timesFlexible} onClick={toggleTimesFlexible} />
                </div>
                <FieldError error={errors.availability} />
            </div>

            {/* Format */}
            <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                    Format<span className="text-accent-400 ml-0.5">*</span> <span className="text-slate-500">(select all that work)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                    {FORMATS.map(f => (
                        <Chip
                            key={f.value}
                            selected={formData.preferred_formats.includes(f.value)}
                            onClick={() => toggleFormat(f.value)}
                        >
                            {f.label}
                        </Chip>
                    ))}
                </div>
                <FieldError error={errors.preferred_formats} />
            </div>

            {/* Experience */}
            <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                    Experience Level<span className="text-accent-400 ml-0.5">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                    {EXPERIENCE.map(e => (
                        <RadioChip
                            key={e.value}
                            selected={formData.experience_level === e.value}
                            onClick={() => setFormData(prev => ({ ...prev, experience_level: e.value }))}
                        >
                            {e.label}
                        </RadioChip>
                    ))}
                </div>
                <FieldError error={errors.experience_level} />
            </div>
        </div>
    );
}

function StepThree({ formData, goToStep }) {
    const dayLabels = formData.preferred_days.map(d => DAYS.find(x => x.value === d)?.label).filter(Boolean).join(', ');
    const timeLabels = formData.preferred_times.map(t => TIMES.find(x => x.value === t)?.label).filter(Boolean).join(', ');
    const formatLabels = formData.preferred_formats.map(f => FORMATS.find(x => x.value === f)?.label).filter(Boolean).join(', ');
    const expLabel = EXPERIENCE.find(e => e.value === formData.experience_level)?.label;

    const allDays = ALL_DAY_VALUES.every(d => formData.preferred_days.includes(d));
    const allTimes = ALL_TIME_VALUES.every(t => formData.preferred_times.includes(t));

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-xl font-bold font-serif text-white mb-1">Review your information</h3>
                <p className="text-slate-400 text-sm">Make sure everything looks right before submitting.</p>
            </div>

            <div className="space-y-3">
                <ReviewRow label="Name" value={`${formData.first_name} ${formData.last_name}`} onEdit={() => goToStep(1)} />
                <ReviewRow label="Phone" value={formData.phone} onEdit={() => goToStep(1)} />
                {formData.email && <ReviewRow label="Email" value={formData.email} onEdit={() => goToStep(1)} />}
                {dayLabels && <ReviewRow label="Preferred Days" value={allDays ? 'Flexible' : dayLabels} onEdit={() => goToStep(2)} />}
                {timeLabels && <ReviewRow label="Preferred Times" value={allTimes ? 'Flexible' : timeLabels} onEdit={() => goToStep(2)} />}
                {formatLabels && <ReviewRow label="Format" value={formatLabels} onEdit={() => goToStep(2)} />}
                <ReviewRow label="Experience" value={expLabel} onEdit={() => goToStep(2)} />
            </div>
        </div>
    );
}

function ReviewRow({ label, value, onEdit }) {
    return (
        <div className="flex items-start justify-between gap-3 py-3 border-b border-slate-700/50 last:border-0">
            <div className="min-w-0">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
                <p className="text-white mt-0.5">{value}</p>
            </div>
            <button
                type="button"
                onClick={onEdit}
                className="text-accent-400 text-sm font-medium hover:text-accent-300 transition-colors flex-shrink-0"
            >
                Edit
            </button>
        </div>
    );
}

// ── Success State ────────────────────────────────────────────────────────────

function SuccessMessage() {
    return (
        <div className="text-center py-8 animate-fade-in">
            <div className="animate-check-circle inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/20 mb-6">
                <svg className="w-10 h-10 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline
                        points="6 12 10 16 18 8"
                        style={{ strokeDasharray: 36, strokeDashoffset: 36 }}
                        className="animate-check-draw"
                    />
                </svg>
            </div>
            <h3 className="text-2xl font-bold font-serif text-white mb-2">You're signed up!</h3>
            <p className="text-slate-400 max-w-sm mx-auto leading-relaxed">
                Thank you for your interest. A member of our team will reach out to you soon with next steps to get you started.
            </p>
        </div>
    );
}

// ── Icon Components ──────────────────────────────────────────────────────────

function CommunityIcon() {
    return (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    );
}

function GrowthIcon() {
    return (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" />
        </svg>
    );
}

function BookIcon() {
    return (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
    );
}

// ── Social Icons ─────────────────────────────────────────────────────────────

function InstagramIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
    );
}

function YouTubeIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
    );
}

// ── Main Page Component ──────────────────────────────────────────────────────

export function BibleStudy() {
    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState('forward');
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        preferred_days: [],
        preferred_times: [],
        preferred_formats: [],
        experience_level: '',
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const formRef = useRef(null);
    const heroCtaRef = useRef(null);
    const [showFloatingCta, setShowFloatingCta] = useState(false);

    // Set body background to match page (prevents edge color bleed on mobile)
    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = '#1e293b'; // slate-800
        return () => { document.body.style.backgroundColor = prev; };
    }, []);

    // Floating CTA visibility on scroll
    useEffect(() => {
        if (!heroCtaRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => setShowFloatingCta(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(heroCtaRef.current);
        return () => observer.disconnect();
    }, []);

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const validateStep1 = () => {
        const errs = {};
        if (!formData.first_name.trim()) errs.first_name = 'First name is required';
        if (!formData.last_name.trim()) errs.last_name = 'Last name is required';
        if (!formData.phone.trim()) errs.phone = 'Phone number is required';
        if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errs.email = 'Please enter a valid email';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateStep2 = () => {
        const errs = {};
        if (formData.preferred_days.length === 0 && formData.preferred_times.length === 0) {
            errs.availability = 'Please select at least one day or time preference';
        }
        if (formData.preferred_formats.length === 0) errs.preferred_formats = 'Please select at least one format';
        if (!formData.experience_level) errs.experience_level = 'Please select your experience level';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const goToStep = (target) => {
        setDirection(target > step ? 'forward' : 'backward');
        setErrors({});
        setStep(target);
    };

    const nextStep = () => {
        if (step === 1 && !validateStep1()) return;
        if (step === 2 && !validateStep2()) return;
        setDirection('forward');
        setErrors({});
        setStep(s => s + 1);
    };

    const prevStep = () => {
        setDirection('backward');
        setErrors({});
        setStep(s => s - 1);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            // Map preferred_formats array to the backend's preferred_format string
            const payload = {
                ...formData,
                preferred_format: formData.preferred_formats.length === 2 ? 'either' : formData.preferred_formats[0] || 'either',
            };
            delete payload.preferred_formats;
            await axios.post(`${API_URL}/api/public/bible-study-signup`, payload);
            setSubmitted(true);
        } catch (err) {
            setSubmitError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-800 text-white overflow-x-hidden">
            {/* ── Nav Bar ─────────────────────────────────────────────── */}
            <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 bg-slate-800/80 backdrop-blur-lg border-b border-slate-700/50">
                <a href="https://revivaltabernacle.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5">
                    <img src="/rt-logo.png" alt="RT" className="w-8 h-8" style={{ filter: 'brightness(0) invert(1)' }} />
                    <span className="text-white/80 text-base font-medium font-serif">Revival Tabernacle</span>
                </a>
                <div className="flex items-center gap-4">
                    <a
                        href="https://www.instagram.com/_revivaltabernacle/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-white transition-colors"
                        aria-label="Instagram"
                    >
                        <InstagramIcon className="w-5 h-5" />
                    </a>
                    <a
                        href="https://www.youtube.com/@joshherringministries4443"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-white transition-colors"
                        aria-label="YouTube"
                    >
                        <YouTubeIcon className="w-5 h-5" />
                    </a>
                </div>
            </header>

            {/* ── Hero Section ────────────────────────────────────────── */}
            <section className="relative min-h-[85vh] flex flex-col items-center justify-center px-4 pt-16 pb-12">
                {/* Background decorative elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-800 to-accent-900/30" />
                    <div className="absolute top-1/4 left-0 w-96 h-96 bg-accent-500/8 rounded-full blur-3xl -translate-x-1/2" />
                    <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent-600/6 rounded-full blur-3xl translate-x-1/2" />
                    {/* Subtle grid pattern */}
                    <div
                        className="absolute inset-0 opacity-[0.04]"
                        style={{
                            backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
                            backgroundSize: '60px 60px',
                        }}
                    />
                </div>

                <div className="relative z-10 text-center max-w-3xl mx-auto">
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-serif tracking-tight leading-[1.1]">
                        Start Your<br />
                        <span className="text-accent-400">Bible Study</span><br />
                        Journey
                    </h1>
                    <p className="mt-5 text-lg sm:text-xl text-slate-400 max-w-xl mx-auto leading-relaxed">
                        Join a group of real people studying scripture together. Whether you're brand new or a lifelong learner, there's a place for you.
                    </p>
                    <button
                        ref={heroCtaRef}
                        onClick={scrollToForm}
                        className="mt-8 inline-flex items-center gap-2.5 px-8 py-4 rounded-xl bg-accent-500 text-white font-semibold text-lg hover:bg-accent-600 transition-colors shadow-lg shadow-accent-500/25"
                    >
                        Sign Up Now
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5v14" /><path d="M19 12l-7 7-7-7" />
                        </svg>
                    </button>
                </div>

                {/* Scroll hint */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-slate-600">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </section>

            {/* ── How It Works ────────────────────────────────────────── */}
            <section className="relative py-16 px-4 bg-slate-750" style={{ backgroundColor: 'rgb(40, 50, 65)' }}>
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl sm:text-4xl font-bold font-serif text-center mb-12">How It Works</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
                        <StepCard number={1} title="Sign Up" description="Fill out a quick form so we know your preferences and availability." />
                        <StepCard number={2} title="Get Matched" description="We'll connect you with a Bible study group that fits your schedule." />
                        <StepCard number={3} title="Start Learning" description="Show up, engage, and grow alongside others in your group." />
                    </div>
                </div>
            </section>

            {/* ── What to Expect ──────────────────────────────────────── */}
            <section className="relative py-16 px-4">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-3xl sm:text-4xl font-bold font-serif text-center mb-4">What to Expect</h2>
                    <p className="text-slate-400 text-center max-w-lg mx-auto mb-12">
                        Our Bible studies are designed to be welcoming, engaging, and transformative.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <FeatureCard
                            icon={<CommunityIcon />}
                            title="Community"
                            description="Connect with a small, supportive group of people who genuinely care about your journey."
                        />
                        <FeatureCard
                            icon={<GrowthIcon />}
                            title="Growth"
                            description="Develop a deeper understanding of scripture through guided discussion and practical application."
                        />
                        <FeatureCard
                            icon={<BookIcon />}
                            title="Understanding"
                            description="Explore the Bible in a way that's approachable and relevant, no matter where you're starting from."
                        />
                    </div>
                </div>
            </section>

            {/* ── Sign-Up Form ────────────────────────────────────────── */}
            <section ref={formRef} className="relative py-16 px-4 scroll-mt-20">
                {/* Background */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-800 via-slate-800/95 to-slate-800" />
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent-500/5 rounded-full blur-3xl" />
                </div>

                <div className="relative z-10 max-w-lg mx-auto">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl sm:text-4xl font-bold font-serif">Get Started</h2>
                        <p className="text-slate-400 mt-2">It only takes a minute to sign up.</p>
                    </div>

                    <div className="bg-slate-700/30 backdrop-blur-sm border border-slate-600/30 rounded-2xl p-6 sm:p-8">
                        {submitted ? (
                            <SuccessMessage />
                        ) : (
                            <>
                                <ProgressBar step={step} total={3} />

                                {/* Step labels */}
                                <div className="flex justify-between mb-6 text-xs font-medium text-slate-500">
                                    <span className={step >= 1 ? 'text-accent-400' : ''}>Your Info</span>
                                    <span className={step >= 2 ? 'text-accent-400' : ''}>Preferences</span>
                                    <span className={step >= 3 ? 'text-accent-400' : ''}>Review</span>
                                </div>

                                {/* Animated step content */}
                                <div
                                    key={step}
                                    className={`min-h-[320px] ${
                                        direction === 'forward' ? 'animate-step-forward' : 'animate-step-backward'
                                    }`}
                                >
                                    {step === 1 && <StepOne formData={formData} setFormData={setFormData} errors={errors} />}
                                    {step === 2 && <StepTwo formData={formData} setFormData={setFormData} errors={errors} />}
                                    {step === 3 && <StepThree formData={formData} goToStep={goToStep} />}
                                </div>

                                {/* Error message */}
                                {submitError && (
                                    <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                                        {submitError}
                                    </div>
                                )}

                                {/* Navigation buttons */}
                                <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-600/30">
                                    {step > 1 ? (
                                        <button
                                            type="button"
                                            onClick={prevStep}
                                            className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors text-sm font-medium"
                                        >
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                                            </svg>
                                            Back
                                        </button>
                                    ) : (
                                        <div />
                                    )}
                                    {step < 3 ? (
                                        <button
                                            type="button"
                                            onClick={nextStep}
                                            className="flex items-center gap-1.5 px-6 py-2.5 rounded-lg bg-accent-500 text-white font-medium hover:bg-accent-600 transition-colors text-sm shadow-lg shadow-accent-500/20"
                                        >
                                            Next
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-accent-500 text-white font-medium hover:bg-accent-600 transition-colors text-sm shadow-lg shadow-accent-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? (
                                                <>
                                                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                    </svg>
                                                    Submitting...
                                                </>
                                            ) : (
                                                'Submit'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* ── Footer ──────────────────────────────────────────────── */}
            <footer className="relative border-t border-slate-700/50 py-8 px-4">
                <div className="flex flex-col items-center gap-4">
                    <a href="https://revivaltabernacle.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-500 hover:text-slate-300 transition-colors text-sm">
                        <img src="/rt-logo.png" alt="RT" className="w-5 h-5" style={{ filter: 'brightness(0) invert(1)', opacity: 0.4 }} />
                        Revival Tabernacle
                    </a>
                    <div className="flex items-center gap-5">
                        <a
                            href="https://www.instagram.com/_revivaltabernacle/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-500 hover:text-slate-300 transition-colors"
                            aria-label="Instagram"
                        >
                            <InstagramIcon className="w-5 h-5" />
                        </a>
                        <a
                            href="https://www.youtube.com/@joshherringministries4443"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-500 hover:text-slate-300 transition-colors"
                            aria-label="YouTube"
                        >
                            <YouTubeIcon className="w-5 h-5" />
                        </a>
                    </div>
                </div>
            </footer>

            {/* ── Floating CTA (mobile) ───────────────────────────────── */}
            <div
                className={`fixed bottom-6 left-4 right-4 z-40 sm:hidden transition-all duration-300 ${
                    showFloatingCta && !submitted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
                }`}
            >
                <button
                    onClick={scrollToForm}
                    className="w-full py-4 rounded-xl bg-accent-500 text-white font-semibold text-lg shadow-2xl shadow-accent-500/30 active:scale-[0.98] transition-transform"
                >
                    Sign Up Now
                </button>
            </div>
        </div>
    );
}

// ── Supporting Components ────────────────────────────────────────────────────

function FeatureCard({ icon, title, description }) {
    return (
        <div className="p-6 rounded-2xl bg-slate-700/30 border border-slate-600/30 hover:bg-slate-700/40 transition-colors">
            <div className="w-14 h-14 rounded-xl bg-accent-500/10 flex items-center justify-center text-accent-400 mb-4">
                {icon}
            </div>
            <h3 className="text-lg font-bold font-serif text-white mb-2">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
        </div>
    );
}

function StepCard({ number, title, description }) {
    return (
        <div className="relative text-center p-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-500 text-white font-bold text-lg mb-4">
                {number}
            </div>
            <h3 className="text-lg font-bold font-serif text-white mb-2">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
        </div>
    );
}

export default BibleStudy;
